/**
 * RNC — TemplateLibrary
 *
 * Lists notification templates from `config.notificationTemplates` (loaded via
 * ConfigContext). Click a row → edit the template (if editor role) or view it
 * (read-only otherwise).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, FilterChips, Pill } from '@/components/primitives';
import { useConfig } from '@/contexts/ConfigContext';
import { useAuth } from '@/contexts/AuthContext';
import type { NotificationChannel } from '@retail-ops/shared-types/enums';
import { canEditRnc } from './roles';

type ChannelFilter = 'all' | NotificationChannel;

export default function TemplateLibrary(): JSX.Element {
  const { config } = useConfig();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [channel, setChannel] = useState<ChannelFilter>('all');

  const templates = useMemo(() => {
    const source = config.notificationTemplates?.templates ?? [];
    if (channel === 'all') return source;
    return source.filter((t) => t.channel === channel);
  }, [config.notificationTemplates?.templates, channel]);

  const editMode = canEditRnc(user?.primaryRole);

  return (
    <div className="rnc-page">
      <header className="rnc-page__header">
        <div className="rnc-page__title-row">
          <h1 className="rnc-page__title">Notification templates</h1>
          <Pill variant="muted">Module RNC</Pill>
          {!editMode && <Pill variant="warn">Read-only</Pill>}
        </div>
        <p className="rnc-page__subtitle">
          Edit the wording of every outbound notification without a code deploy.
          Every save creates a new version row.
        </p>
      </header>

      <div style={{ display: 'flex', gap: 'var(--sp-6)', alignItems: 'center', flexWrap: 'wrap' }}>
        <FilterChips
          label="Channel"
          options={[
            { value: 'all', label: `All (${config.notificationTemplates?.templates?.length ?? 0})` },
            { value: 'email', label: 'Email' },
            { value: 'teams', label: 'Teams' },
            { value: 'sms', label: 'SMS' },
            { value: 'physical', label: 'Physical' },
          ]}
          selected={channel}
          onChange={(v) => setChannel(v as ChannelFilter)}
        />
        <div style={{ marginLeft: 'auto' }}>
          <Button variant="ghost" size="sm" onClick={() => navigate('/rnc')}>
            Back to dashboard
          </Button>
        </div>
      </div>

      <section className="rnc-panel">
        {templates.length === 0 ? (
          <div className="rnc-banner rnc-banner--warn">
            No notification templates are seeded yet. Once the `/v3/config/notification-templates`
            route returns data, rows will appear here.
          </div>
        ) : (
          <ul className="rnc-template-list">
            {templates.map((t) => (
              <li
                key={t.templateName}
                tabIndex={0}
                role="button"
                className="rnc-template-row"
                onClick={() =>
                  navigate(`/rnc/templates/${encodeURIComponent(t.templateName)}`)
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    navigate(`/rnc/templates/${encodeURIComponent(t.templateName)}`);
                  }
                }}
              >
                <span className="rnc-template-row__name">{t.templateName}</span>
                <Pill variant={t.isActive ? 'ok' : 'muted'}>
                  {t.isActive ? 'Active' : 'Inactive'}
                </Pill>
                <span className="rnc-template-row__meta">{t.channel}</span>
                <span className="rnc-template-row__meta">v{t.currentVersion}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
